<template>
  <div class="seo-edit">
    <h4 class="mb-3">SEO 설정</h4>

    <!-- 로딩 중일 때 표시 -->
    <div v-if="loading" class="loading-container">
      <b-spinner variant="primary"></b-spinner>
      <p>{{ progressMessage || 'SEO 설정 작업 중...' }}</p>
    </div>

    <!-- 기본 입력 영역 -->
    <div v-else class="initial-state-container">

      <!-- AI 자동 생성 섹션 -->
      <div class="card mb-4">
        <div class="card-header bg-light">
          <h5 class="mb-0">AI 자동 생성</h5>
        </div>
        <div class="card-body">
          <p>AI가 상품 정보를 분석하여 최적의 SEO 설정을 자동으로 생성합니다.</p>

          <div class="row">
            <div class="col-md-4">
              <div class="form-group">
                <label>브랜드</label>
                <input type="text" class="form-control" v-model="brandName" @blur="updateBrandName"
                  :placeholder="defaultBrand || '브랜드명을 입력하세요 (예: 쿠플러스 등등)'" />
                <small class="form-text text-muted">
                  기본 설정의 브랜드가 자동으로 입력됩니다
                </small>
              </div>
            </div>

            <div class="col-md-4">
              <div class="form-group">
                <label>상품 특징</label>
                <input type="text" class="form-control" v-model="productFeature" @blur="updateProductFeature"
                  placeholder="상품 특징을 입력하세요 (예: 인조가죽 등)" />
                <small class="form-text text-muted">
                  상품명에 포함될 주요 특징을 입력하세요
                </small>
              </div>
            </div>

            <div class="col-md-4">
              <div class="form-group">
                <label>타겟 키워드</label>
                <input type="text" class="form-control" v-model="aiTargetKeyword" @blur="updateTargetKeyword"
                  placeholder="핵심 타겟 키워드를 입력하세요 (예: 크로스백 등)" />
                <small class="form-text text-muted">
                  이 키워드를 중심으로 SEO 최적화가 진행됩니다
                </small>
              </div>
            </div>
          </div>

          <div class="alert alert-info mt-3">
            <p class="mb-1"><strong>생성될 상품명 예시:</strong></p>
            <p class="mb-0">{{ brandName || defaultBrand || '쿠플러스' }} {{ productFeature || '인조가죽' }} {{ aiTargetKeyword
              || '크로스백' }}</p>
          </div>

          <div class="text-center mt-3">
            <b-button variant="outline-primary" @click="startAiGeneration" :disabled="!canStartAiGeneration">
              <i class="las la-robot"></i> AI로 자동 생성
            </b-button>
          </div>
        </div>
      </div>

      <div class="row mb-4">
        <!-- 상품명 및 검색 키워드 -->
        <div class="col-md-6">
          <div class="card">
            <div class="card-header bg-light">
              <h5 class="mb-0">상품명 / 검색태그</h5>
            </div>
            <div class="card-body">
              <div class="form-group">
                <label>상품명</label>
                <input type="text" class="form-control" v-model="seoData.productName" placeholder="검색 최적화된 상품명을 입력하세요"
                  maxlength="100" />
                <div class="text-muted mt-1">
                  <small>{{ seoData.productName.length }}/100자</small>
                </div>
              </div>

              <div class="form-group">
                <label>검색태그 (쉼표로 구분)</label>
                <textarea class="form-control" v-model="keywordsText" rows="3" maxlength="150"
                  placeholder="키워드1, 키워드2, 키워드3 형태로 입력하세요"></textarea>
                <div class="d-flex justify-content-between mt-1">
                  <small class="form-text text-muted">
                    검색에 노출되길 원하는 키워드를 쉼표로 구분하여 입력하세요 (최대 150자)
                  </small>
                  <small
                    :class="{ 'text-danger': keywordsText.length > 140, 'text-warning': keywordsText.length > 120 && keywordsText.length <= 140, 'text-muted': keywordsText.length <= 120 }">
                    {{ keywordsText.length }}/150자
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 옵션명 설정 -->
        <div class="col-md-6">
          <div class="card">
            <div class="card-header bg-light">
              <h5 class="mb-0">옵션명</h5>
            </div>
            <div class="card-body">
              <div class="options-container">
                <div v-if="!productOptions || productOptions.length === 0" class="text-center p-4">
                  <p class="text-muted">옵션 정보가 없습니다.</p>
                </div>
                <div v-else class="options-list">
                  <div v-for="(option, index) in productOptions" :key="index"
                    class="option-item mb-3 p-2 border-bottom">
                    <div class="d-flex">
                      <div class="option-image mr-3">
                        <img :src="getOptionImage(option)" class="img-thumbnail" style="width: 60px; height: 60px;"
                          alt="옵션 이미지">
                      </div>
                      <div class="option-details flex-grow-1">
                        <div class="option-original mb-1">
                          <small class="text-muted">원본: {{ getOptionOriginalName(option) }}</small>
                        </div>
                        <div class="form-group mb-0">
                          <input type="text" class="form-control" v-model="option.korName" placeholder="한글 옵션명 입력" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>



      <!-- 저장 버튼 -->
      <div class="action-button-container text-center mt-4">
        <b-button variant="primary" size="lg" @click="saveSeoData" :disabled="!canSave">
          <i class="las la-save"></i> SEO 설정 저장
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
// import store from '@/store';
import seoService from '../../services/works/seoService';

export default {
  name: "SeoEdit",
  data() {
    return {
      // AI 자동 생성 옵션
      aiTargetKeyword: '',
      brandName: '',
      productFeature: '',

      // 수동 입력 데이터
      targetKeyword: '',
      keywordsText: '',

      // 옵션 데이터
      productOptions: [],

      // SEO 데이터
      seoData: {
        productName: '',
        keywords: []
      }
    };
  },
  computed: {
    // Vuex 상태 매핑
    ...mapState({
      product: state => state.selectedRocketProduct,
      selectedOption: state => state.selectedOption,
      selectedWorkType: state => state.selectedWorkType,
      processing: state => state.processing,
      progressMessage: state => state.progressMessage
    }),
    defaultBrand() {
      const brand = this.$store.state.settings[0]?.defaultSettings?.brand || '';
      // 쉼표로 구분된 경우 첫 번째 브랜드명만 반환
      return brand.includes(',') ? brand.split(',')[0].trim() : brand;
    },

    // 로딩 상태
    loading() {
      return this.processing;
    },

    // 저장 가능 여부 체크
    canSave() {
      return !!this.seoData.productName.trim();
    },

    // AI 생성 시작 가능 여부 체크
    canStartAiGeneration() {
      return !!this.aiTargetKeyword.trim();
    }
  },
  mounted() {
    // 초기 데이터 설정
    this.initializeData();
  },
  methods: {
    // Vuex 액션 매핑
    ...mapActions([
      'updateRocketProduct',
      'setProcessing'
    ]),

    // 초기 데이터 설정
    initializeData() {
      if (this.product) {
        // 타겟 키워드 초기화
        this.targetKeyword = this.product.targetKeyword || '';
        this.aiTargetKeyword = this.product.targetKeyword || '';

        // 브랜드명 초기화 (첫 번째 브랜드명만 사용)
        let productBrand = this.product.brand || '';
        if (productBrand.includes(',')) {
          productBrand = productBrand.split(',')[0].trim();
        }
        this.brandName = this.defaultBrand || productBrand;

        // 상품 특징 초기화
        this.productFeature = this.product.productFeature || '';

        // 상품명 초기화
        this.seoData.productName = this.product.productNameTrans || this.product.productName || '';

        // 기존 SEO 데이터가 있으면 로드
        if (this.product.seo) {
          this.seoData.productName = this.product.seo.productName || this.seoData.productName;
          this.seoData.keywords = this.product.seo.keywords || [];
          this.keywordsText = this.seoData.keywords.join(', ');
        } else {
          this.keywordsText = this.targetKeyword || '';
        }

        // 옵션 데이터 초기화
        this.initializeOptions();
      }
    },

    // 옵션 데이터 초기화
    initializeOptions() {
      if (this.product && this.product.options && this.product.options.length > 0) {
        // 깊은 복사로 옵션 데이터 복사
        this.productOptions = JSON.parse(JSON.stringify(this.product.options)).map(option => {
          // korName이 없으면 추가
          if (!option.korName) {
            option.korName = '';
          }
          return option;
        });
      } else {
        this.productOptions = [];
      }
    },

    // 옵션 이미지 가져오기
    getOptionImage(option) {
      // mainImageStatus에서 이미지 가져오기
      if (option.mainImageStatus && option.mainImageStatus.image) {
        return option.mainImageStatus.image;
      }
      // option1에서 이미지 가져오기
      else if (option.option1 && option.option1.imageUrl) {
        return option.option1.imageUrl;
      }
      // 기본 이미지 반환
      return "";
    },

    // 옵션 원본 이름 가져오기
    getOptionOriginalName(option) {
      let originalName = '';

      // option1 정보 추가
      if (option.option1) {
        originalName += option.option1.name || '';
        // option1 번역 정보가 있으면 추가
        if (option.option1.nameTrans) {
          originalName += ` (${option.option1.nameTrans})`;
        }
      }

      // option2 정보가 있으면 추가
      if (option.option2) {
        originalName += ' / ' + (option.option2.name || '');
        // option2 번역 정보가 있으면 추가
        if (option.option2.nameTrans) {
          originalName += ` (${option.option2.nameTrans})`;
        }
      }

      return originalName || '옵션 정보 없음';
    },

    // 입력 즉시 store 업데이트 메서드들
    updateBrandName() {
      if (this.product && this.product._id) {
        this.$store.commit('updateProductPartial', {
          productId: this.product._id,
          updateFields: {
            brand: this.brandName
          }
        });
      }
    },

    updateProductFeature() {
      if (this.product && this.product._id) {
        this.$store.commit('updateProductPartial', {
          productId: this.product._id,
          updateFields: {
            productFeature: this.productFeature
          }
        });
      }
    },

    updateTargetKeyword() {
      if (this.product && this.product._id) {
        this.$store.commit('updateProductPartial', {
          productId: this.product._id,
          updateFields: {
            targetKeyword: this.aiTargetKeyword
          }
        });
      }
    },

    // AI 자동 생성 시작
    async startAiGeneration() {
      if (!this.canStartAiGeneration) return;

      try {
        // 입력값들을 먼저 상품 정보에 저장
        this.updateBrandName();
        this.updateProductFeature();
        this.updateTargetKeyword();

        // seoService.process() 호출 (기본 AI 모드)
        const result = await seoService.process(this.product);

        if (result.status === 'completed') {
          this.$bvToast.toast('SEO 정보가 성공적으로 생성되었습니다.', {
            title: '성공',
            variant: 'success',
            solid: true,
            toaster: 'b-toaster-bottom-right'
          });

          // 데이터 새로고침
          this.initializeData();
        } else if (result.status === 'error') {
          this.$bvToast.toast(`SEO 생성 중 오류: ${result.error}`, {
            title: '오류',
            variant: 'danger',
            solid: true,
            toaster: 'b-toaster-bottom-right'
          });
        }
      } catch (error) {
        console.error('SEO 생성 중 오류:', error);
        this.$bvToast.toast(`SEO 생성 중 오류: ${error.message}`, {
          title: '오류',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-bottom-right'
        });
      }
    },



    // SEO 설정 저장
    async saveSeoData() {
      if (!this.product || !this.canSave) return;

      try {
        // 로딩 상태 시작
        this.setProcessing({
          isProcessing: true,
          message: 'SEO 설정 저장 중...'
        });

        // 키워드 처리
        let keywordsText = this.keywordsText;

        // 검색 태그 길이가 150자를 초과하는 경우 처리
        if (keywordsText.length > 150) {
          console.log(`검색 태그 길이(${keywordsText.length})가 150자를 초과합니다. 태그를 줄입니다.`);

          // 검색 태그를 배열로 분리
          const tagsArray = keywordsText.split(',').map(tag => tag.trim());

          // 150자 이하로 줄이기 위해 뒤에서부터 태그 제거
          let trimmedTags = '';
          for (let i = 0; i < tagsArray.length; i++) {
            const potentialTags = tagsArray.slice(0, tagsArray.length - i).join(', ');
            if (potentialTags.length <= 150) {
              trimmedTags = potentialTags;
              console.log(`원래 태그 수: ${tagsArray.length}, 줄인 태그 수: ${tagsArray.length - i}`);
              break;
            }
          }

          keywordsText = trimmedTags;
          console.log(`최종 검색 태그 길이: ${keywordsText.length}`);

          // UI에 적용
          this.keywordsText = keywordsText;
        }

        // 최종 키워드 배열 생성
        const keywords = keywordsText
          .split(',')
          .map(keyword => keyword.trim())
          .filter(keyword => keyword.length > 0);

        // SEO 데이터 구성
        const seoData = {
          productName: this.seoData.productName,
          keywords: keywords,
          options: [],
          // 상품 특징 칸은 지금까지 화면(store)에만 반영되고 서버로 가지 않아 새로고침하면 옛 값이 돌아왔다.
          // 저장 버튼에서 같이 보낸다 — 비운 칸은 빈 값으로 저장된다.
          productFeature: (this.productFeature || '').trim()
        };

        // 옵션 정보 구성
        if (this.productOptions && this.productOptions.length > 0) {
          seoData.options = this.productOptions
            .filter(option => option.optionNumber)
            .map(option => ({
              optionNumber: option.optionNumber,
              korName: option.korName || ''
            }));
        }

        console.log('저장할 SEO 데이터:', seoData);

        // seoService를 사용하여 부분 업데이트 수행
        try {
          console.log('===== SEO 부분 업데이트 시작 =====');

          // 저장 전 coupangDoc 확인 (디버깅용)
          if (this.product.coupangDoc && this.product.coupangDoc.jsonDocument) {
            console.log(`저장 전 coupangDoc.jsonDocument 배열 길이: ${this.product.coupangDoc.jsonDocument.length}`);

            // 각 옵션별 jsonDocument 항목 확인
            this.product.coupangDoc.jsonDocument.forEach((docItem, index) => {
              if (docItem.startPage) {
                console.log(`저장 전 jsonDocument[${index}] 상품명: ${docItem.startPage.productName || '없음'}`);
              }
            });
          }

          // seoService를 통한 부분 업데이트
          await seoService.updateProductWithCoupangDocPartial(this.product, seoData);

          console.log('SEO 정보 부분 업데이트 성공');

          // 업데이트 후 상태 확인 (디버깅용)
          const updatedProduct = this.$store.state.selectedRocketProduct;
          if (updatedProduct && updatedProduct.coupangDoc && updatedProduct.coupangDoc.jsonDocument) {
            console.log(`업데이트 후 coupangDoc.jsonDocument 배열 길이: ${updatedProduct.coupangDoc.jsonDocument.length}`);

            // 각 옵션별 jsonDocument 항목 확인
            updatedProduct.coupangDoc.jsonDocument.forEach((docItem, index) => {
              if (docItem.startPage) {
                console.log(`업데이트 후 jsonDocument[${index}] 상품명: ${docItem.startPage.productName || '없음'}`);
              }
            });
          }

          console.log('===== SEO 부분 업데이트 완료 =====');
        } catch (error) {
          console.error('SEO 정보 저장 실패:', error);
          throw error;
        }

        // 성공 메시지
        this.$bvToast.toast('SEO 설정이 저장되었습니다.', {
          title: '성공',
          variant: 'success',
          solid: true,
          toaster: 'b-toaster-bottom-right'
        });
      } catch (error) {
        console.error('SEO 설정 저장 중 오류:', error);

        // 오류 메시지
        this.$bvToast.toast('SEO 설정 저장 중 오류가 발생했습니다.', {
          title: '오류',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-bottom-right'
        });
      } finally {
        // 로딩 상태 종료
        this.setProcessing({ isProcessing: false });
      }
    }
  },
  watch: {
    // 제품 변경 감지
    product: {
      handler(newVal) {
        if (newVal) {
          this.initializeData();
        }
      },
      deep: true
    },
    // 진행 메시지 변경 감지
    progressMessage(newMessage) {
      // SEO 작업 완료 메시지인 경우
      if (newMessage.includes('SEO 정보가 성공적으로 생성되었습니다')) {
        // 토스트 메시지 표시
        this.$bvToast.toast(newMessage, {
          title: '성공',
          variant: 'success',
          solid: true,
          toaster: 'b-toaster-bottom-right'
        });

        // 컴포넌트 데이터 새로고침 (필요한 경우)
        this.initializeData();
      }
      // 오류 메시지인 경우
      else if (newMessage.includes('오류')) {
        this.$bvToast.toast(newMessage, {
          title: '오류',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-bottom-right'
        });
      }
    },
  }
}
</script>

<style scoped>
.seo-edit {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

/* 로딩 컨테이너 */
.loading-container {
  text-align: center;
  padding: 40px 0;
}

/* 초기 상태 */
.initial-state-container {
  background-color: #fff;
  padding: 20px;
}

/* 옵션 리스트 스타일 */
.options-list {
  max-height: 400px;
  overflow-y: auto;
  padding-right: 10px;
}

.option-item {
  transition: background-color 0.2s;
}

.option-item:hover {
  background-color: #f8f9fa;
}

.option-image img {
  object-fit: cover;
}

/* 키워드 태그 */
.keyword-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.keyword-tag {
  background-color: #e9f5ff;
  color: #0366d6;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.9rem;
}

/* 액션 버튼 */
.action-button-container {
  margin-top: 30px;
}
</style>
